import DeleteOutlined from '@material-ui/icons/DeleteOutlined'
import { useTheme } from '@material-ui/core/styles'
import classnames from 'classnames'
import ScheduleDialog from 'elements/scheduleDialog/ScheduleDialog'
import { defaultSchedule } from 'elements/scheduleDialog/utils'
import _ from 'lodash'
import { IconButton, Tooltip } from 'opensolar-ui'
import { Button as RaButton } from 'ra-ui-materialui'
import { FC, useEffect, useState } from 'react'
import { SelectInput, TextInput, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import Schedule from 'resources/batterySchemes/Schedule'
import { ADJUSTMENT_OPTIONS, SINGLE_TIER_OPTIONS } from 'resources/utilityTariffs/common'
import { tableStyles } from 'resources/utilityTariffs/styles'
import RatePeriodTiers from './RatePeriodTiers'
import { getTariffInputOptions } from './inputOptions'
import { TOUPeriod } from './types'
import { ChargeHeadlineDescription } from './ChargeHeadlineDescription'

type PropsType = {
  rateList: any[]
  source: string
  rateIndex: number
  allowSetSchedule: boolean
  addRatePeriod: () => void
  disabled?: boolean
  chargeCategories?: ChargeCat[]
}

type ChargeCat = {
  id: string
  name: string
}

const RatePeriods: FC<PropsType> = ({
  rateList,
  source,
  rateIndex,
  allowSetSchedule,
  addRatePeriod,
  disabled,
  chargeCategories,
}) => {
  const form = useForm()
  const classes = tableStyles()
  const translate = useTranslate()
  const rate = rateList[rateIndex]
  const rateSource = `${source}.${rateIndex}`
  const ratePeriods = rate?.tou_periods
  const deleteRatePeriod = (periodIndex) => {
    let currList = [...ratePeriods]
    currList.splice(periodIndex, 1)
    form.mutators.updateField(`${rateSource}.tou_periods`, currList)
  }
  useEffect(() => {
    if (
      editingSchedule !== undefined &&
      ratePeriods?.length === 1 &&
      !_.isEqual(ratePeriods[0].schedule, defaultSchedule())
    ) {
      addRatePeriod()
      setEditingSchedule(undefined)
    }
  }, [ratePeriods])

  const [editingSchedule, setEditingSchedule] = useState<number | undefined>(undefined)
  return (
    <div className={classes.periodRow}>
      {ratePeriods ? (
        <>
          {ratePeriods?.map((ratePeriod, periodIndex) => (
            <RateInfo
              rateSource={source}
              rateFieldPath={rateSource}
              dataSource={`${rateSource}.tou_periods.${periodIndex}`}
              infoItem={ratePeriod}
              periodIndex={periodIndex}
              periodList={ratePeriods}
              deleteRatePeriod={() => deleteRatePeriod(periodIndex)}
              allowSetSchedule={allowSetSchedule}
              editSchedule={() => {
                setEditingSchedule(periodIndex)
              }}
              disabled={disabled}
              chargeCategories={chargeCategories}
            />
          ))}
          {editingSchedule !== undefined && (
            <ScheduleDialog
              isOpen={true}
              onClose={() => {
                setEditingSchedule(undefined)
              }}
              handleSave={(state) => {
                let newPeriods = _.cloneDeep(ratePeriods)
                newPeriods[editingSchedule] = { ...newPeriods[editingSchedule], schedule: state }
                form.change(`${rateSource}.tou_periods`, newPeriods)
                if (newPeriods.length !== 1 || _.isEqual(state, defaultSchedule())) {
                  setEditingSchedule(undefined)
                }
              }}
              dialogTitle={translate('Time-of-Use Period - ') + ratePeriods[editingSchedule].name}
              allSchedules={ratePeriods?.map((x) => {
                return x.schedule
              })}
              editingIndex={editingSchedule}
            />
          )}
        </>
      ) : (
        <RateInfo
          allowSetSchedule={false}
          rateSource={source}
          dataSource={rateSource}
          rateFieldPath={rateSource}
          infoItem={rate}
          disabled={disabled}
          chargeCategories={chargeCategories}
        />
      )}
    </div>
  )
}

type RateInfoType = {
  rateSource: string
  rateFieldPath: string
  dataSource: string
  infoItem?: any
  deleteRatePeriod?: any
  allowSetSchedule: boolean
  periodList?: TOUPeriod[]
  periodIndex?: number
  editSchedule?: any
  disabled?: boolean
  chargeCategories?: ChargeCat[]
}

const RateInfo: FC<RateInfoType> = ({
  rateFieldPath,
  dataSource,
  rateSource,
  infoItem,
  deleteRatePeriod,
  allowSetSchedule,
  periodIndex,
  periodList,
  editSchedule,
  disabled,
  chargeCategories,
}) => {
  const classes = tableStyles()
  const theme = useTheme()
  const translate = useTranslate()
  const sourceName = rateSource.replace('tariffData.', '')
  const adjustment = ADJUSTMENT_OPTIONS.includes(sourceName)
  const isSingleTier = SINGLE_TIER_OPTIONS.includes(sourceName)
  const showOptionalChargeNoteUnderRateName = periodList == null || periodIndex === 0

  return (
    <div className={`${classes.ratePeriod} ${periodList && periodList?.length > 1 ? 'periodRow' : ''}`}>
      <div className={classes.ratePeriodContent}>
        <div className={classes.rateNameRow}>
          <span className={classes.rateNameLabel}>{translate(adjustment ? 'Name' : 'Rate Name')}</span>
          <div className={classes.rateNameInputRow}>
          <div className={classes.rateNameInputWrapper}>
            <div className={`${infoItem?.blocks?.length > 1 ? classes.multiTier : ''} ${classes.rateName}`} style={{ flex: 1, minWidth: 120 }}>
              <TextInput
                fullWidth
                className={classnames(classes.rateInput, classes.rateRow)}
                variant="outlined"
                label={false}
                source={`${dataSource}.name`}
                options={getTariffInputOptions(theme, { InputLabelProps: { shrink: true } })}
                fullwidth
                disabled={disabled}
              />
            </div>
            {chargeCategories && (
              <div style={{ minWidth: 120 }}>
                <SelectInput
                  className={classnames(classes.rateInput, classes.rateRow)}
                  label={false}
                  variant="outlined"
                  source={`${dataSource}.charge_type`}
                  options={getTariffInputOptions(theme, { InputLabelProps: { shrink: true } })}
                  choices={chargeCategories}
                  parse={(value) => (value === 'null' ? undefined : value)}
                  format={(value) => value || 'null'}
                  translateChoice={false}
                />
              </div>
            )}
          </div>
          <div className={classes.ratePeriodDelete}>
            {allowSetSchedule && periodList?.length !== 1 ? (
              <Tooltip title={translate('Remove rate period')}>
                <span className={classes.ratePeriodDeleteIconOnly}>
                  <IconButton onClick={deleteRatePeriod} className={classes.deleteBtn} disabled={disabled}>
                    <DeleteOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <div style={{ width: 32 }} />
            )}
          </div>
          </div>
        </div>
        {showOptionalChargeNoteUnderRateName && (
          <ChargeHeadlineDescription rateFieldPath={rateFieldPath} disabled={disabled} />
        )}
        <div className={classes.tierAndScheduleRow}>
        <div className={classes.tierSection} style={{ marginBottom: infoItem?.blocks?.length > 1 ? 'auto' : 'unset' }}>
          <div
            className={
              isSingleTier ? `${classes.tierHeaderRow} ${classes.tierHeaderRowSingleTier}` : classes.tierHeaderRow
            }
          >
            <div className={classes.tierLabelColumn} />
            <div className={classes.tierInputWithLabel}>
              <span className={classes.periodHeaderLabel}>
                {translate(adjustment ? 'Amount %' : 'Price')}
              </span>
            </div>
            {!adjustment && !isSingleTier && (
              <div className={classes.midInputContainer}>
                <span className={classes.periodHeaderLabel}>{translate('Tier Quantity')}</span>
              </div>
            )}
            <div className={adjustment ? classes.adjustmentContainer : classes.tierInput}>
              <span className={classes.periodHeaderLabel}>
                {translate(adjustment ? 'Adjustment To' : 'Units')}
              </span>
            </div>
            <div className={classes.deleteContainer} />
          </div>
          <div className={classes.tierContent}>
            <RatePeriodTiers
              tiers={infoItem.blocks || [{ value: infoItem.value, units: infoItem.units, limit: undefined }]}
              source={rateSource}
              tierSource={infoItem.blocks ? `${dataSource}.blocks` : dataSource}
              adjustment={adjustment}
              disabled={disabled}
              isSingleTier={isSingleTier}
            />
          </div>
        </div>
        {allowSetSchedule && (
          <div className={classes.scheduleSection}>
            <span className={classes.periodHeaderLabel}>{translate('Applicable Date & Time')}</span>
            <div className={`${classes.row} ${classes.scheduleContainer} ${classes.scheduleContent}`}>
              {periodList && periodIndex !== undefined && (
                <Schedule itemList={periodList} itemIndex={periodIndex} onEdit={editSchedule} disabled={disabled} />
              )}
            </div>
          </div>
        )}
        </div>
        {allowSetSchedule && periodList?.length !== 1 && (
          <div className={classes.ratePeriodBottomActions}>
            <span className={classes.ratePeriodDeleteFullWidthBtn}>
              <RaButton
                onClick={deleteRatePeriod}
                startIcon={<DeleteOutlined />}
                label={translate('Remove rate period')}
                hideLabelAt={null}
                variant="outlined"
                color="default"
                disabled={disabled}
                className={classes.deleteRatePeriodFullWidth}
              />
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default RatePeriods
